/**
 * KizunaAI voices guide: preset voices, cloning your own voice, speech speed.
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app; the ones not retaken
 * for v0.44.0 are from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Choose a Voice or Clone Your Own',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'When Sokuji speaks a translation, KizunaAI uses a voice: for your translation when <strong>Translation the other side hears</strong> is on (Me or Both mode), and for the other side’s when <strong>Translation I hear</strong> is on. You can pick one of about 200 preset voices, or clone your own voice from a short recording so that the other side hears the translation in your voice. All of this is in <strong>Settings → Advanced → Provider</strong>, and describes the Soniox engine; the Qwen engine’s voices are covered in the last question below. The screenshots are from Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Open the voice settings',
      content:
        'Open <strong>Settings</strong>, switch to <strong>Advanced</strong> with the button at the top right of the panel, and choose the <strong>Provider</strong> tab. <strong>Voice Settings</strong> shows the voice in use, with a short description of how it sounds. The <strong>Speech</strong> block leads there too: under each spoken row, <strong>Voice</strong> is followed by the voice’s name, and clicking the name opens Voice Settings.',
      screenshot: '01-voice.png',
    },
    {
      title: 'Browse and filter the preset voices',
      content:
        'Click the voice to open the list. Narrow it down by gender, age, accent, use case and style, and press the play button to hear a sample. Previews are synthesized for you and charged to your account balance.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Add your own voice',
      content:
        'Under <strong>Custom voices (0/1)</strong>, choose <strong>Add a voice…</strong>. Import an audio file with <strong>Import voice…</strong>, record straight away with <strong>Record voice…</strong>, or drop a file onto the dialog. The clip must be between 3 seconds and 2 minutes long, and at most 35 MB.',
      screenshot: '03-add-voice.png',
      tip: 'A clear recording of you speaking naturally, in a quiet room, gives the best result.',
    },
    {
      title: 'Listen, confirm and clone',
      content:
        'Listen to the clip, tick <strong>I confirm I have the right to use this voice</strong> and click <strong>Clone voice</strong>. The recording is sent to Kizuna AI and passed on to Soniox to build your voice. It is not stored on Kizuna AI’s servers; it stays on this device so your voice can be rebuilt later.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'Use “My voice”',
      content:
        'Your clone appears as <strong>My voice</strong> under <strong>Custom voices (1/1)</strong>. Select it like any preset; the play button previews it and the bin deletes it.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Replace your voice',
      content:
        'Each account has one custom voice. While you have it, the list reads <strong>Custom voices (1/1)</strong> and offers no <strong>Add a voice…</strong>. To use a new recording, delete <strong>My voice</strong> with the bin first: <strong>Add a voice…</strong> then comes back.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Choose the voice you hear',
      content:
        'While the other side’s translation is read to you (<strong>Translation I hear</strong> on), Voice Settings has two rows. <strong>Voice the other side hears</strong> is the voice of your translation: a preset or My voice. <strong>Voice I hear</strong> reads the other person’s translation to you and offers the preset voices only. By default the two are different voices, so you can tell the two people apart; if you pick the same voice for both, a note under Voice I hear names the voice that reads instead.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Adjust the speech speed',
      content:
        'Under <strong>Speech Synthesis (TTS) Settings</strong>, <strong>Speech Speed</strong> makes the translated voice slower or faster, from 0.7× to 1.3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Tips',
    items: [
      'A voice is only used when a translation is spoken. With Translation the other side hears and Translation I hear both switched off, no voice is needed.',
      'Building a custom voice and previewing voices need a balance on your account.',
      'Settings are locked while a session runs. Stop the session to change the voice.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Why does a session use a built-in voice instead of My voice?',
        answer:
          'Sokuji tells you when this happens and why. The most common reasons: this device has no copy of your recording (record it again in Settings to use your voice on this computer), all custom voice slots are busy for the moment, or the voice could not be built from the clip (try a clearer recording). The session continues with a built-in voice.',
      },
      {
        question: 'Does my recording have to be in the language my translation is spoken in?',
        answer:
          'No. Your voice is not tied to the language of the recording: cloned from you speaking English, it still speaks your Spanish translation in your voice. If you can, record yourself speaking the language your translation is spoken in: it then sounds the most like you speaking that language.',
      },
      {
        question: 'Can I clone someone else’s voice?',
        answer: 'Only with their permission. You confirm that you have the right to use the voice before it is cloned.',
      },
      {
        question: 'Can the other person’s translation be read in my cloned voice?',
        answer: 'No. Voice I hear offers the preset voices only. Your cloned voice speaks your own translation.',
      },
      {
        question: 'Does my voice work in every region?',
        answer:
          'With the Soniox engine, custom voices belong to one region, and each region remembers its own voice choice. If you change the region, add your voice again there. Voice I hear is one choice for every region. The Qwen engine’s voices work in both of its regions.',
      },
      {
        question: 'What about the Qwen engine?',
        answer:
          'With KizunaAI Powered by Qwen, Voice Settings offers 47 preset voices and two cloning choices: <strong>Clone the speaker’s voice</strong> clones your voice live on every turn, and <strong>Clone the first speaker’s voice</strong> clones it once at the start and keeps it — nothing to record, nothing stored, no custom-voice slot. Every preset speaks every language Qwen can speak. Voice I hear picks the voice for the other side the same way, cloning included.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '选择声音或克隆你自己的声音',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      'Sokuji 朗读翻译时，KizunaAI 会使用一个声音：<strong>对方听到的翻译</strong>打开时（我或两者模式）用于你的翻译，<strong>我听到的翻译</strong>打开时用于对方的翻译。你可以从约 200 个预设声音中选择一个，也可以用一段简短的录音克隆你自己的声音，让对方用你的声音听到翻译。这些都位于<strong>设置 → 高级 → 提供商</strong>中，本页介绍的是 Soniox 引擎；Qwen 引擎的声音见下面最后一个问题。截图来自 Sokuji 0.44.0。',
  },
  steps: [
    {
      title: '打开语音设置',
      content:
        '打开<strong>设置</strong>，用面板右上角的按钮切换到<strong>高级</strong>，然后选择<strong>提供商</strong>标签页。<strong>语音设置</strong>会显示当前使用的声音，并附有对其音色的简短描述。也可以从<strong>语音</strong>部分进入：每个会朗读的行下方都有“<strong>声音</strong>”和声音名称，点击名称即可打开语音设置。',
      screenshot: '01-voice.png',
    },
    {
      title: '浏览和筛选预设声音',
      content:
        '点击该声音即可打开列表。可以按性别、年龄、口音、用途和风格筛选，点击播放按钮试听样本。试听内容是为你实时合成的，费用从你的账户余额中扣除。',
      screenshot: '02-voice-picker.png',
    },
    {
      title: '添加你自己的声音',
      content:
        '在<strong>自定义声音（0/1）</strong>下选择<strong>添加声音…</strong>。可以用<strong>导入声音…</strong>导入音频文件，用<strong>录制声音…</strong>直接录音，或者把文件拖放到对话框中。片段长度必须在 3 秒到 2 分钟之间，大小不超过 35 MB。',
      screenshot: '03-add-voice.png',
      tip: '在安静的房间里自然地说话、录得清晰，效果最好。',
    },
    {
      title: '试听、确认并克隆',
      content:
        '试听片段，勾选<strong>我确认我有权使用此声音</strong>，然后点击<strong>克隆声音</strong>。录音会发送给 Kizuna AI，并转交给 Soniox 以生成你的声音。它不会存储在 Kizuna AI 的服务器上，而是保留在本设备上，以便日后重新生成你的声音。',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: '使用“我的声音”',
      content:
        '你克隆的声音会以<strong>我的声音</strong>出现在<strong>自定义声音（1/1）</strong>下。像选择预设声音一样选中它即可；播放按钮用于试听，垃圾桶按钮用于删除。',
      screenshot: '05-my-voice.png',
    },
    {
      title: '更换你的声音',
      content:
        '每个账号只有一个自定义声音。拥有它期间，列表会显示<strong>自定义声音（1/1）</strong>，也不再提供<strong>添加声音…</strong>。要使用新的录音，请先用垃圾桶按钮删除<strong>我的声音</strong>，<strong>添加声音…</strong>随后就会重新出现。',
      screenshot: '06-replace-hint.png',
    },
    {
      title: '选择你听到的声音',
      content:
        '当对方的翻译会朗读给你听时（<strong>我听到的翻译</strong>已打开），语音设置中有两行。<strong>对方听到的声音</strong>是你的翻译所用的声音：预设声音或“我的声音”。<strong>我听到的声音</strong>用来把对方的翻译朗读给你听，只能选择预设声音。默认情况下两者是不同的声音，方便你分辨两个人；如果你给两者选了同一个声音，“我听到的声音”下方会注明改用哪个声音朗读。',
      screenshot: '08-two-voices.png',
    },
    {
      title: '调整语速',
      content:
        '在<strong>语音合成（TTS）设置</strong>下，<strong>语速</strong>可以让翻译语音变慢或变快，范围为 0.7× 到 1.3×。',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: '提示',
    items: [
      '只有在朗读翻译时才会用到声音。“对方听到的翻译”和“我听到的翻译”都关闭时，不需要声音。',
      '创建自定义声音和试听声音都需要账户中有余额。',
      '会话运行期间设置会被锁定。请先停止会话，再更换声音。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '为什么会话使用的是内置声音，而不是“我的声音”？',
        answer:
          '出现这种情况时，Sokuji 会告诉你并说明原因。最常见的原因有：本设备上没有你的录音副本（在设置中重新录制，即可在这台电脑上使用你的声音）；所有自定义声音名额暂时都在使用中；或者无法用该片段生成声音（请尝试更清晰的录音）。会话会继续使用内置声音。',
      },
      {
        question: '录音必须用译文的语言吗？',
        answer:
          '不必。克隆出的声音不受录音语言的限制：用中文录音克隆，它照样能用你的声音说出英语译文。如果可以，就用译文的语言录音，这样听起来最像你本人在说那种语言。',
      },
      {
        question: '我可以克隆别人的声音吗？',
        answer: '只有在对方许可的情况下才可以。克隆之前，你需要确认自己有权使用该声音。',
      },
      {
        question: '对方的翻译能用我克隆的声音朗读吗？',
        answer: '不能。“我听到的声音”只能选择预设声音。你克隆的声音用来朗读你自己的翻译。',
      },
      {
        question: '我的声音在所有区域都能用吗？',
        answer:
          '使用 Soniox 引擎时，自定义声音只属于一个区域，每个区域会分别记住自己的声音选择。如果更改了区域，请在新区域重新添加你的声音。“我听到的声音”则是所有区域共用的一个选择。Qwen 引擎的声音在它的两个区域都能用。',
      },
      {
        question: 'Qwen 引擎的声音是怎样的？',
        answer:
          '使用 KizunaAI（Qwen 驱动）时，语音设置提供 47 个预设声音和两种克隆选择：<strong>复刻说话人的声音</strong>会在每一轮实时复刻你的声音，<strong>复刻第一位说话人的声音</strong>只在开始时复刻一次并一直沿用——无需录音，不保存任何内容，也不占用自定义声音名额。每个预设声音都能说 Qwen 能说的所有语言。<strong>我听到的声音</strong>以同样的方式为对方选择声音，也包括复刻。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '音声を選ぶ・自分の声を複製する',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      'Sokuji が翻訳を読み上げるとき、KizunaAI は音声を使います。<strong>相手に聞こえる翻訳</strong>がオンのとき（自分モードまたは両方モード）はあなたの翻訳に、<strong>自分に聞こえる翻訳</strong>がオンのときは相手の翻訳に使います。約 200 種類のプリセット音声から選ぶことも、短い録音から自分の声を複製して、相手にあなたの声で翻訳を聞かせることもできます。これらはすべて<strong>設定 → 詳細 → プロバイダー</strong>にあり、Soniox エンジンについて説明しています。Qwen エンジンの音声は、下の最後の質問で説明します。スクリーンショットは Sokuji 0.44.0 のものです。',
  },
  steps: [
    {
      title: '音声設定を開く',
      content:
        '<strong>設定</strong>を開き、パネル右上のボタンで<strong>詳細</strong>に切り替えて、<strong>プロバイダー</strong>タブを選びます。<strong>音声設定</strong>に、使用中の音声と、その声の特徴の短い説明が表示されます。<strong>音声</strong>セクションからも開けます。読み上げる各行の下では<strong>音声</strong>の後に音声名が続き、その名前をクリックすると音声設定が開きます。',
      screenshot: '01-voice.png',
    },
    {
      title: 'プリセット音声を探して絞り込む',
      content:
        '音声をクリックすると一覧が開きます。性別、年齢、アクセント、用途、スタイルで絞り込み、再生ボタンでサンプルを試聴できます。試聴音声はその都度合成され、アカウントの残高から差し引かれます。',
      screenshot: '02-voice-picker.png',
    },
    {
      title: '自分の声を追加する',
      content:
        '<strong>カスタム音声（0/1）</strong>で<strong>音声を追加…</strong>を選びます。<strong>音声をインポート…</strong>で音声ファイルを取り込むか、<strong>音声を録音…</strong>でその場で録音するか、ファイルをダイアログにドロップします。クリップの長さは 3 秒から 2 分まで、サイズは 35 MB 以下にしてください。',
      screenshot: '03-add-voice.png',
      tip: '静かな部屋で自然に話した、はっきりした録音がいちばんよい結果になります。',
    },
    {
      title: '聞いて確認し、複製する',
      content:
        'クリップを聞き、<strong>この音声を使用する権利があることを確認します</strong>にチェックを入れて、<strong>音声を複製</strong>をクリックします。録音は Kizuna AI に送信され、あなたの声を作成するために Soniox に渡されます。Kizuna AI のサーバーには保存されず、あとで声を作り直せるようにこの端末上に残ります。',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: '「マイ音声」を使う',
      content:
        '複製した声は、<strong>カスタム音声（1/1）</strong>の下に<strong>マイ音声</strong>として表示されます。プリセットと同じように選択できます。再生ボタンで試聴し、ゴミ箱ボタンで削除します。',
      screenshot: '05-my-voice.png',
    },
    {
      title: '声を置き換える',
      content:
        'カスタム音声はアカウントごとに 1 つです。声があるあいだは、一覧に<strong>カスタム音声（1/1）</strong>と表示され、<strong>音声を追加…</strong>は表示されません。新しい録音を使うには、まずゴミ箱ボタンで<strong>マイ音声</strong>を削除してください。すると<strong>音声を追加…</strong>が再び表示されます。',
      screenshot: '06-replace-hint.png',
    },
    {
      title: '自分に聞こえる音声を選ぶ',
      content:
        '相手の翻訳があなたに読み上げられるとき（<strong>自分に聞こえる翻訳</strong>がオン）、音声設定には 2 つの行があります。<strong>相手に聞こえる音声</strong>はあなたの翻訳の音声で、プリセットかマイ音声を選べます。<strong>自分に聞こえる音声</strong>は相手の翻訳をあなたに読み上げる音声で、プリセット音声だけから選べます。既定では 2 つは別の音声なので、2 人の話を聞き分けられます。両方に同じ音声を選ぶと、「自分に聞こえる音声」の下に、代わりに読み上げる音声の名前が表示されます。',
      screenshot: '08-two-voices.png',
    },
    {
      title: '話す速さを調整する',
      content:
        '<strong>音声合成（TTS）設定</strong>の<strong>話速</strong>で、翻訳音声を 0.7× から 1.3× の範囲で遅くしたり速くしたりできます。',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'ヒント',
    items: [
      '音声が使われるのは、翻訳を読み上げるときだけです。「相手に聞こえる翻訳」と「自分に聞こえる翻訳」がどちらもオフのときは、音声は必要ありません。',
      'カスタム音声の作成と音声の試聴には、アカウントの残高が必要です。',
      'セッションの実行中は設定がロックされます。音声を変更するには、セッションを停止してください。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: 'セッションで「マイ音声」ではなく内蔵の音声が使われるのはなぜですか？',
        answer:
          'その場合、Sokuji がそのことと理由をお知らせします。よくある原因は、この端末に録音のコピーがない（このパソコンで自分の声を使うには、設定でもう一度録音してください）、カスタム音声の枠が一時的にすべて使用中、またはクリップから声を作成できなかった（よりはっきりした録音を試してください）のいずれかです。セッションは内蔵の音声で続行されます。',
      },
      {
        question: '録音は翻訳先の言語でないといけませんか？',
        answer:
          'いいえ。複製した声は録音した言語に縛られません。日本語の録音から複製しても、英語の翻訳をあなたの声で話します。可能であれば翻訳先の言語で話して録音してください。その言語をあなた自身が話しているように、最も自然に聞こえます。',
      },
      {
        question: '他人の声を複製できますか？',
        answer: '本人の許可がある場合に限ります。複製する前に、その声を使用する権利があることを確認します。',
      },
      {
        question: '相手の翻訳を、複製した自分の声で読み上げられますか？',
        answer: 'いいえ。「自分に聞こえる音声」ではプリセット音声だけを選べます。複製した声は、あなた自身の翻訳を話します。',
      },
      {
        question: '自分の声はどのリージョンでも使えますか？',
        answer:
          'Soniox エンジンでは、カスタム音声は 1 つのリージョンに属し、音声の選択もリージョンごとに記憶されます。リージョンを変更した場合は、そのリージョンでもう一度声を追加してください。「自分に聞こえる音声」は、すべてのリージョンで共通の 1 つの選択です。Qwen エンジンの音声は、2 つのリージョンのどちらでも使えます。',
      },
      {
        question: 'Qwen エンジンではどうなりますか？',
        answer:
          'KizunaAI（Qwen 提供）では、音声設定に 47 種類のプリセット音声と 2 つの複製の選択肢があります。<strong>話者の声を複製</strong>はターンごとにあなたの声をその場で複製し、<strong>最初の話者の声を複製</strong>は最初に 1 回だけ複製してそのまま使い続けます。どちらも録音は不要で、何も保存されず、カスタム音声の枠も使いません。どのプリセットも、Qwen が話せるすべての言語で話せます。「自分に聞こえる音声」も、複製を含めて同じ方法で相手側の音声を選びます。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '음성 선택 또는 내 목소리 복제',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      'Sokuji가 번역을 음성으로 들려줄 때 KizunaAI는 음성을 사용합니다. <strong>상대방에게 들리는 번역</strong>이 켜져 있으면(나 또는 모두 모드) 내 번역에, <strong>내가 듣는 번역</strong>이 켜져 있으면 상대방의 번역에 음성이 쓰입니다. 약 200개의 프리셋 음성 중 하나를 고르거나, 짧은 녹음으로 내 목소리를 복제해 상대방이 번역을 내 목소리로 듣게 할 수 있습니다. 모두 <strong>설정 → 고급 → 제공자</strong>에 있으며, Soniox 엔진에 해당하는 내용입니다. Qwen 엔진의 음성은 아래 마지막 질문에서 다룹니다. 스크린샷은 Sokuji 0.44.0에서 촬영했습니다.',
  },
  steps: [
    {
      title: '음성 설정 열기',
      content:
        '<strong>설정</strong>을 열고 패널 오른쪽 위의 버튼으로 <strong>고급</strong>으로 전환한 다음 <strong>제공자</strong> 탭을 선택하세요. <strong>음성 설정</strong>에 사용 중인 음성과 그 음색에 대한 짧은 설명이 표시됩니다. <strong>음성</strong> 섹션에서도 이곳으로 갈 수 있습니다. 음성으로 나오는 각 행 아래에 <strong>음성</strong>과 그 뒤에 음성 이름이 표시되며, 이름을 클릭하면 음성 설정이 열립니다.',
      screenshot: '01-voice.png',
    },
    {
      title: '프리셋 음성 둘러보기 및 필터링',
      content:
        '음성을 클릭하면 목록이 열립니다. 성별, 나이, 억양, 용도, 스타일로 범위를 좁히고, 재생 버튼을 눌러 샘플을 들어 보세요. 미리 듣기는 그때그때 합성되며 계정 잔액에서 차감됩니다.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: '내 음성 추가',
      content:
        '<strong>사용자 정의 음성 (0/1)</strong>에서 <strong>음성 추가…</strong>를 선택하세요. <strong>음성 가져오기…</strong>로 오디오 파일을 가져오거나, <strong>음성 녹음…</strong>으로 바로 녹음하거나, 파일을 대화 상자에 끌어다 놓으면 됩니다. 클립 길이는 3초에서 2분 사이여야 하며, 크기는 35 MB 이하여야 합니다.',
      screenshot: '03-add-voice.png',
      tip: '조용한 방에서 자연스럽게 말한 깨끗한 녹음이 가장 좋은 결과를 냅니다.',
    },
    {
      title: '듣고, 확인하고, 복제하기',
      content:
        '클립을 들어 보고 <strong>이 음성을 사용할 권리가 있음을 확인합니다</strong>에 체크한 다음 <strong>음성 복제</strong>를 클릭하세요. 녹음은 Kizuna AI로 전송된 후 음성을 만들기 위해 Soniox로 전달됩니다. Kizuna AI 서버에는 저장되지 않으며, 나중에 음성을 다시 만들 수 있도록 이 기기에 남습니다.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: '“내 음성” 사용',
      content:
        '복제한 음성은 <strong>사용자 정의 음성 (1/1)</strong> 아래에 <strong>내 음성</strong>으로 표시됩니다. 프리셋처럼 선택하면 되며, 재생 버튼으로 미리 듣고 휴지통 버튼으로 삭제합니다.',
      screenshot: '05-my-voice.png',
    },
    {
      title: '음성 교체',
      content:
        '계정마다 사용자 정의 음성은 하나입니다. 음성이 있는 동안에는 목록에 <strong>사용자 정의 음성 (1/1)</strong>이라고 표시되고 <strong>음성 추가…</strong>가 나타나지 않습니다. 새 녹음을 쓰려면 먼저 휴지통 버튼으로 <strong>내 음성</strong>을 삭제하세요. 그러면 <strong>음성 추가…</strong>가 다시 나타납니다.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: '내가 듣는 음성 선택',
      content:
        '상대방의 번역을 나에게 읽어 주는 동안(<strong>내가 듣는 번역</strong>이 켜져 있을 때) 음성 설정에는 두 개의 행이 있습니다. <strong>상대방에게 들리는 음성</strong>은 내 번역의 음성으로, 프리셋 또는 내 음성입니다. <strong>내가 듣는 음성</strong>은 상대방의 번역을 나에게 읽어 주며, 프리셋 음성만 고를 수 있습니다. 기본적으로 두 음성은 서로 다르므로 두 사람을 구별할 수 있습니다. 둘 다 같은 음성을 고르면 “내가 듣는 음성” 아래에 대신 읽어 줄 음성을 알려 주는 안내가 표시됩니다.',
      screenshot: '08-two-voices.png',
    },
    {
      title: '말하기 속도 조정',
      content:
        '<strong>음성 합성(TTS) 설정</strong>의 <strong>말하기 속도</strong>로 번역된 음성을 0.7×에서 1.3×까지 느리게 또는 빠르게 할 수 있습니다.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: '팁',
    items: [
      '음성은 번역을 소리 내어 들려줄 때만 쓰입니다. “상대방에게 들리는 번역”과 “내가 듣는 번역”을 모두 끄면 음성이 필요 없습니다.',
      '사용자 정의 음성을 만들거나 음성을 미리 들으려면 계정에 잔액이 있어야 합니다.',
      '세션이 실행 중일 때는 설정이 잠깁니다. 음성을 바꾸려면 세션을 중지하세요.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '세션에서 내 음성 대신 기본 제공 음성이 쓰이는 이유는 무엇인가요?',
        answer:
          '이런 경우 Sokuji가 그 사실과 이유를 알려 줍니다. 가장 흔한 이유는 이 기기에 녹음 사본이 없거나(이 컴퓨터에서 내 음성을 쓰려면 설정에서 다시 녹음하세요), 사용자 정의 음성 슬롯이 잠시 모두 사용 중이거나, 클립으로 음성을 만들 수 없었던 경우(더 깨끗한 녹음으로 시도해 보세요)입니다. 세션은 기본 제공 음성으로 계속됩니다.',
      },
      {
        question: '녹음은 번역되는 언어로 해야 하나요?',
        answer:
          '아니요. 복제한 음성은 녹음한 언어에 묶이지 않습니다. 한국어로 녹음해 복제해도 영어 번역을 내 목소리로 말합니다. 가능하다면 번역되는 언어로 말한 녹음을 사용하세요. 그 언어를 직접 말하는 것처럼 가장 자연스럽게 들립니다.',
      },
      {
        question: '다른 사람의 목소리를 복제할 수 있나요?',
        answer: '본인의 허락이 있을 때만 가능합니다. 복제하기 전에 그 음성을 사용할 권리가 있음을 확인합니다.',
      },
      {
        question: '상대방의 번역을 내가 복제한 목소리로 읽어 줄 수 있나요?',
        answer: '아니요. “내가 듣는 음성”에서는 프리셋 음성만 고를 수 있습니다. 복제한 목소리는 내 번역을 말합니다.',
      },
      {
        question: '내 음성은 모든 지역에서 작동하나요?',
        answer:
          'Soniox 엔진에서는 사용자 정의 음성이 하나의 지역에 속하며, 지역마다 음성 선택을 따로 기억합니다. 지역을 바꾸면 그 지역에서 음성을 다시 추가하세요. “내가 듣는 음성”은 모든 지역에 공통으로 적용되는 하나의 선택입니다. Qwen 엔진의 음성은 두 지역 모두에서 작동합니다.',
      },
      {
        question: 'Qwen 엔진은 어떤가요?',
        answer:
          'KizunaAI(Qwen 제공)를 사용하면 음성 설정에서 47개의 프리셋 음성과 두 가지 복제 선택지를 고를 수 있습니다. <strong>화자 목소리 복제</strong>는 매 턴마다 내 목소리를 실시간으로 복제하고, <strong>첫 번째 화자 목소리 복제</strong>는 처음에 한 번 복제한 뒤 그대로 유지합니다. 녹음할 필요도, 저장되는 것도, 사용자 정의 음성 슬롯도 없습니다. 모든 프리셋은 Qwen이 말할 수 있는 모든 언어를 말합니다. “내가 듣는 음성”도 상대방 쪽 음성을 복제를 포함해 같은 방식으로 고릅니다.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Elige una voz o clona la tuya',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'Cuando Sokuji lee en voz alta una traducción, KizunaAI usa una voz: para tu traducción cuando <strong>Traducción que oye la otra parte</strong> está activada (modo Yo o Ambos), y para la de la otra parte cuando <strong>Traducción que yo oigo</strong> está activada. Puedes elegir una de unas 200 voces predefinidas o clonar tu propia voz a partir de una grabación corta, para que la otra parte oiga la traducción con tu voz. Todo esto está en <strong>Configuración → Avanzado → Proveedor</strong> y describe el motor de Soniox; las voces del motor de Qwen se explican en la última pregunta de abajo. Las capturas son de Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Abre los ajustes de voz',
      content:
        'Abre <strong>Configuración</strong>, cambia a <strong>Avanzado</strong> con el botón de la parte superior derecha del panel y elige la pestaña <strong>Proveedor</strong>. <strong>Configuración de Voz</strong> muestra la voz en uso, con una breve descripción de cómo suena. La sección <strong>Voz</strong> también lleva hasta allí: bajo cada fila de traducción hablada, <strong>Voz</strong> va seguido del nombre de la voz, y al hacer clic en el nombre se abre Configuración de Voz.',
      screenshot: '01-voice.png',
    },
    {
      title: 'Explora y filtra las voces predefinidas',
      content:
        'Haz clic en la voz para abrir la lista. Fíltrala por género, edad, acento, uso y estilo, y pulsa el botón de reproducción para escuchar una muestra. Las muestras se sintetizan para ti y se cobran de tu saldo.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Añade tu propia voz',
      content:
        'En <strong>Voces personalizadas (0/1)</strong>, elige <strong>Añadir una voz…</strong>. Importa un archivo de audio con <strong>Importar voz…</strong>, graba al momento con <strong>Grabar voz…</strong> o suelta un archivo en el cuadro de diálogo. El clip debe durar entre 3 segundos y 2 minutos, y ocupar como máximo 35 MB.',
      screenshot: '03-add-voice.png',
      tip: 'Una grabación clara en la que hables con naturalidad, en una habitación silenciosa, da el mejor resultado.',
    },
    {
      title: 'Escucha, confirma y clona',
      content:
        'Escucha el clip, marca <strong>Confirmo que tengo derecho a usar esta voz</strong> y haz clic en <strong>Clonar voz</strong>. La grabación se envía a Kizuna AI y se transmite a Soniox para crear tu voz. No se almacena en los servidores de Kizuna AI: permanece en este dispositivo para que tu voz se pueda volver a crear más adelante.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'Usa «Mi voz»',
      content:
        'Tu clon aparece como <strong>Mi voz</strong> en <strong>Voces personalizadas (1/1)</strong>. Selecciónala como cualquier voz predefinida; el botón de reproducción la reproduce y la papelera la elimina.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Sustituye tu voz',
      content:
        'Cada cuenta tiene una sola voz personalizada. Mientras la tienes, la lista muestra <strong>Voces personalizadas (1/1)</strong> y no ofrece <strong>Añadir una voz…</strong>. Para usar una grabación nueva, elimina primero <strong>Mi voz</strong> con la papelera: entonces vuelve a aparecer <strong>Añadir una voz…</strong>.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Elige la voz que oyes',
      content:
        'Mientras se te lee la traducción de la otra parte (<strong>Traducción que yo oigo</strong> activada), Configuración de Voz tiene dos filas. <strong>Voz que oye la otra parte</strong> es la voz de tu traducción: una voz predefinida o Mi voz. <strong>Voz que yo oigo</strong> te lee la traducción de la otra persona y solo ofrece las voces predefinidas. De forma predeterminada son dos voces distintas, para que puedas distinguir a las dos personas; si eliges la misma voz para ambas, una nota bajo Voz que yo oigo indica la voz que lee en su lugar.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Ajusta la velocidad del habla',
      content:
        'En <strong>Configuración Síntesis de voz (TTS)</strong>, <strong>Velocidad del habla</strong> hace que la voz traducida sea más lenta o más rápida, de 0,7× a 1,3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Consejos',
    items: [
      'Una voz solo se usa cuando una traducción se lee en voz alta. Con Traducción que oye la otra parte y Traducción que yo oigo desactivadas, no hace falta ninguna voz.',
      'Crear una voz personalizada y escuchar muestras de voces requiere saldo en tu cuenta.',
      'Los ajustes se bloquean mientras hay una sesión en marcha. Detén la sesión para cambiar la voz.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Por qué una sesión usa una voz integrada en lugar de Mi voz?',
        answer:
          'Sokuji te avisa cuando ocurre y te dice por qué. Los motivos más habituales: este dispositivo no tiene una copia de tu grabación (vuelve a grabarla en Configuración para usar tu voz en este ordenador), todos los espacios para voces personalizadas están ocupados en este momento, o no se pudo crear la voz a partir del clip (prueba con una grabación más clara). La sesión continúa con una voz integrada.',
      },
      {
        question: '¿La grabación tiene que estar en el idioma de mi traducción?',
        answer:
          'No. Tu voz no depende del idioma de la grabación: si la clonas hablando en español, seguirá diciendo tu traducción al inglés con tu voz. Si puedes, grábate hablando en el idioma de tu traducción: así suena más como tú hablando ese idioma.',
      },
      {
        question: '¿Puedo clonar la voz de otra persona?',
        answer: 'Solo con su permiso. Antes de clonarla, confirmas que tienes derecho a usar esa voz.',
      },
      {
        question: '¿Se puede leer la traducción de la otra persona con mi voz clonada?',
        answer: 'No. Voz que yo oigo solo ofrece las voces predefinidas. Tu voz clonada dice tu propia traducción.',
      },
      {
        question: '¿Funciona mi voz en todas las regiones?',
        answer:
          'Con el motor de Soniox, las voces personalizadas pertenecen a una sola región, y cada región recuerda su propia elección de voz. Si cambias de región, vuelve a añadir tu voz allí. Voz que yo oigo es una sola elección para todas las regiones. Las voces del motor de Qwen funcionan en sus dos regiones.',
      },
      {
        question: '¿Y el motor de Qwen?',
        answer:
          'Con KizunaAI (Con tecnología de Qwen), Configuración de Voz ofrece 47 voces predefinidas y dos opciones de clonación: <strong>Clonar la voz del hablante</strong> clona tu voz en vivo en cada turno, y <strong>Clonar la voz del primer hablante</strong> la clona una sola vez al principio y la conserva; no hay nada que grabar, nada se almacena y no ocupa ningún espacio de voz personalizada. Cada voz predefinida habla todos los idiomas que Qwen puede hablar. Voz que yo oigo elige la voz de la otra parte del mismo modo, clonación incluida.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Choisir une voix ou cloner la vôtre',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'Quand Sokuji prononce une traduction, KizunaAI utilise une voix : pour votre traduction quand <strong>Traduction entendue par l\'autre</strong> est activée (mode Moi ou Les deux), et pour celle de l’autre partie quand <strong>Traduction que j\'entends</strong> est activée. Vous pouvez choisir l’une des quelque 200 voix prédéfinies, ou cloner votre propre voix à partir d’un court enregistrement pour que votre interlocuteur entende la traduction avec votre voix. Tout cela se trouve dans <strong>Paramètres → Avancé → Fournisseur</strong> et décrit le moteur Soniox ; les voix du moteur Qwen sont traitées dans la dernière question ci-dessous. Les captures d’écran proviennent de Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Ouvrez les paramètres de voix',
      content:
        'Ouvrez les <strong>Paramètres</strong>, passez en <strong>Avancé</strong> avec le bouton en haut à droite du panneau, puis choisissez l’onglet <strong>Fournisseur</strong>. <strong>Paramètres de Voix</strong> affiche la voix utilisée, avec une courte description de son timbre. La section <strong>Voix</strong> y mène aussi : sous chaque ligne parlée, le libellé <strong>Voix</strong> est suivi du nom de la voix, et un clic sur ce nom ouvre Paramètres de Voix.',
      screenshot: '01-voice.png',
    },
    {
      title: 'Parcourez et filtrez les voix prédéfinies',
      content:
        'Cliquez sur la voix pour ouvrir la liste. Filtrez-la par genre, âge, accent, usage et style, et appuyez sur le bouton de lecture pour écouter un extrait. Les extraits sont synthétisés pour vous et débités de votre solde.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Ajoutez votre propre voix',
      content:
        'Sous <strong>Voix personnalisées (0/1)</strong>, choisissez <strong>Ajouter une voix…</strong>. Importez un fichier audio avec <strong>Importer une voix…</strong>, enregistrez directement avec <strong>Enregistrer une voix…</strong>, ou déposez un fichier dans la boîte de dialogue. L’extrait doit durer entre 3 secondes et 2 minutes, et peser 35 Mo au maximum.',
      screenshot: '03-add-voice.png',
      tip: 'Un enregistrement net, où vous parlez naturellement dans une pièce calme, donne le meilleur résultat.',
    },
    {
      title: 'Écoutez, confirmez et clonez',
      content:
        'Écoutez l’extrait, cochez <strong>Je confirme avoir le droit d\'utiliser cette voix</strong> et cliquez sur <strong>Cloner une voix</strong>. L’enregistrement est envoyé à Kizuna AI puis transmis à Soniox pour créer votre voix. Il n’est pas stocké sur les serveurs de Kizuna AI : il reste sur cet appareil afin que votre voix puisse être recréée plus tard.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'Utilisez « Ma voix »',
      content:
        'Votre clone apparaît sous le nom <strong>Ma voix</strong> dans <strong>Voix personnalisées (1/1)</strong>. Sélectionnez-la comme n’importe quelle voix prédéfinie ; le bouton de lecture permet de l’écouter et la corbeille de la supprimer.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Remplacez votre voix',
      content:
        'Chaque compte dispose d’une seule voix personnalisée. Tant que vous l’avez, la liste affiche <strong>Voix personnalisées (1/1)</strong> et ne propose pas <strong>Ajouter une voix…</strong>. Pour utiliser un nouvel enregistrement, supprimez d’abord <strong>Ma voix</strong> avec la corbeille : <strong>Ajouter une voix…</strong> réapparaît alors.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Choisissez la voix que vous entendez',
      content:
        'Pendant que la traduction de l’autre partie vous est lue (<strong>Traduction que j\'entends</strong> activée), Paramètres de Voix comporte deux lignes. <strong>Voix entendue par l\'autre</strong> est la voix de votre traduction : une voix prédéfinie ou Ma voix. <strong>Voix que j\'entends</strong> vous lit la traduction de votre interlocuteur et ne propose que les voix prédéfinies. Par défaut, ce sont deux voix différentes, pour que vous puissiez distinguer les deux personnes ; si vous choisissez la même voix pour les deux, une note sous Voix que j\'entends indique la voix qui lit à sa place.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Réglez la vitesse de parole',
      content:
        'Sous <strong>Paramètres Synthèse vocale (TTS)</strong>, <strong>Vitesse de parole</strong> rend la voix traduite plus lente ou plus rapide, de 0,7× à 1,3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Conseils',
    items: [
      'Une voix n’est utilisée que lorsqu’une traduction est prononcée. Avec « Traduction entendue par l\'autre » et « Traduction que j\'entends » toutes deux désactivées, aucune voix n’est nécessaire.',
      'La création d’une voix personnalisée et l’écoute des voix nécessitent un solde sur votre compte.',
      'Les paramètres sont verrouillés pendant une session. Arrêtez la session pour changer de voix.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Pourquoi une session utilise-t-elle une voix intégrée au lieu de Ma voix ?',
        answer:
          'Sokuji vous prévient quand cela arrive et vous en donne la raison. Les causes les plus fréquentes : cet appareil n’a pas de copie de votre enregistrement (enregistrez-le à nouveau dans les Paramètres pour utiliser votre voix sur cet ordinateur), tous les emplacements de voix personnalisées sont occupés pour le moment, ou la voix n’a pas pu être créée à partir de l’extrait (essayez un enregistrement plus net). La session continue avec une voix intégrée.',
      },
      {
        question: 'Mon enregistrement doit-il être dans la langue de ma traduction ?',
        answer:
          'Non. Votre voix ne dépend pas de la langue de l’enregistrement : clonée à partir de vous parlant français, elle dit quand même votre traduction anglaise avec votre voix. Si vous le pouvez, enregistrez-vous dans la langue de votre traduction : elle ressemble alors le plus à vous parlant cette langue.',
      },
      {
        question: 'Puis-je cloner la voix de quelqu’un d’autre ?',
        answer: 'Seulement avec son autorisation. Vous confirmez avoir le droit d’utiliser la voix avant qu’elle ne soit clonée.',
      },
      {
        question: 'La traduction de mon interlocuteur peut-elle être lue avec ma voix clonée ?',
        answer: 'Non. « Voix que j\'entends » ne propose que les voix prédéfinies. Votre voix clonée prononce votre propre traduction.',
      },
      {
        question: 'Ma voix fonctionne-t-elle dans toutes les régions ?',
        answer:
          'Avec le moteur Soniox, une voix personnalisée appartient à une seule région, et chaque région mémorise son propre choix de voix. Si vous changez de région, ajoutez de nouveau votre voix dans celle-ci. « Voix que j\'entends » est un choix unique pour toutes les régions. Les voix du moteur Qwen fonctionnent dans ses deux régions.',
      },
      {
        question: 'Et le moteur Qwen ?',
        answer:
          'Avec KizunaAI — Propulsé par Qwen, Paramètres de Voix propose 47 voix prédéfinies et deux choix de clonage : <strong>Cloner la voix de l\'orateur</strong> clone votre voix en direct à chaque tour de parole, et <strong>Cloner la voix du premier orateur</strong> la clone une fois au début et la conserve — rien à enregistrer, rien de stocké, aucun emplacement de voix personnalisée. Chaque voix prédéfinie parle toutes les langues que Qwen sait parler. Voix que j\'entends choisit la voix de l’autre partie de la même façon, clonage compris.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'اختر صوتًا أو استنسخ صوتك',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'عندما ينطق Sokuji ترجمةً، يستخدم KizunaAI صوتًا: لترجمتك عندما يكون خيار <strong>الترجمة التي يسمعها الطرف الآخر</strong> مفعّلًا (في وضع أنا أو الكل)، ولترجمة الطرف الآخر عندما يكون خيار <strong>الترجمة التي أسمعها</strong> مفعّلًا. يمكنك اختيار أحد نحو 200 صوت جاهز، أو استنساخ صوتك من تسجيل قصير حتى يسمع الطرف الآخر الترجمة بصوتك. كل ذلك موجود في <strong>الإعدادات ← متقدم ← المزود</strong>، وهو يصف محرك Soniox؛ أما أصوات محرك Qwen فيغطيها السؤال الأخير أدناه. لقطات الشاشة مأخوذة من Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'افتح إعدادات الصوت',
      content:
        'افتح <strong>الإعدادات</strong>، وانتقل إلى <strong>متقدم</strong> بالزر الموجود أعلى يمين اللوحة، ثم اختر علامة التبويب <strong>المزود</strong>. يعرض قسم <strong>إعدادات الصوت</strong> الصوت المستخدم مع وصف قصير لطابعه. ويقودك قسم <strong>الكلام</strong> إلى هناك أيضًا: تحت كل صف منطوق، تأتي كلمة <strong>الصوت</strong> متبوعةً باسم الصوت، والنقر على الاسم يفتح «إعدادات الصوت».',
      screenshot: '01-voice.png',
    },
    {
      title: 'تصفّح الأصوات الجاهزة وصفِّها',
      content:
        'انقر على الصوت لفتح القائمة. ضيّق النتائج حسب الجنس والعمر واللكنة والاستخدام والأسلوب، واضغط زر التشغيل لسماع عيّنة. تُولَّد المعاينات خصيصًا لك وتُخصم من رصيد حسابك.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'أضف صوتك',
      content:
        'تحت <strong>الأصوات المخصصة (0/1)</strong>، اختر <strong>إضافة صوت…</strong>. استورد ملفًا صوتيًا عبر <strong>استيراد صوت…</strong>، أو سجّل مباشرة عبر <strong>تسجيل صوت…</strong>، أو أفلت ملفًا في مربع الحوار. يجب أن تكون مدة المقطع من 3 ثوانٍ إلى 2 دقيقة، وألا يتجاوز حجمه 35 MB.',
      screenshot: '03-add-voice.png',
      tip: 'يمنحك تسجيل واضح تتحدث فيه بشكل طبيعي في غرفة هادئة أفضل نتيجة.',
    },
    {
      title: 'استمع وأكّد واستنسخ',
      content:
        'استمع إلى المقطع، وحدّد <strong>أؤكد أن لدي الحق في استخدام هذا الصوت</strong> وانقر على <strong>استنساخ الصوت</strong>. يُرسَل التسجيل إلى Kizuna AI ثم يُمرَّر إلى Soniox لبناء صوتك. لا يُخزَّن على خوادم Kizuna AI؛ بل يبقى على هذا الجهاز حتى يمكن إعادة بناء صوتك لاحقًا.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'استخدم «صوتي»',
      content:
        'يظهر صوتك المستنسخ باسم <strong>صوتي</strong> تحت <strong>الأصوات المخصصة (1/1)</strong>. اختره كأي صوت جاهز؛ يتيح لك زر التشغيل معاينته، وتحذفه سلة المهملات.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'استبدل صوتك',
      content:
        'لكل حساب صوت مخصص واحد. ما دام لديك هذا الصوت، تعرض القائمة <strong>الأصوات المخصصة (1/1)</strong> ولا تعرض <strong>إضافة صوت…</strong>. لاستخدام تسجيل جديد، احذف <strong>صوتي</strong> بسلة المهملات أولًا: عندها يعود <strong>إضافة صوت…</strong> للظهور.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'اختر الصوت الذي تسمعه',
      content:
        'عندما تُقرأ عليك ترجمة الطرف الآخر (مع تفعيل <strong>الترجمة التي أسمعها</strong>)، يضم قسم «إعدادات الصوت» صفين. <strong>الصوت الذي يسمعه الطرف الآخر</strong> هو صوت ترجمتك: صوت جاهز أو «صوتي». أما <strong>الصوت الذي أسمعه</strong> فيقرأ عليك ترجمة الشخص الآخر ولا يعرض إلا الأصوات الجاهزة. افتراضيًا يكون الصوتان مختلفين، حتى تميّز بين الشخصين؛ وإذا اخترت الصوت نفسه للاثنين، فستذكر ملاحظة تحت «الصوت الذي أسمعه» اسم الصوت الذي يقرأ بدلًا منه.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'اضبط سرعة الكلام',
      content:
        'تحت <strong>إعدادات تحويل النص إلى كلام (TTS)</strong>، يجعل خيار <strong>سرعة الكلام</strong> الصوت المترجَم أبطأ أو أسرع، من 0.7× إلى 1.3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'نصائح',
    items: [
      'لا يُستخدم الصوت إلا عند نطق ترجمة. عند إيقاف «الترجمة التي يسمعها الطرف الآخر» و«الترجمة التي أسمعها» كليهما، لا حاجة إلى صوت.',
      'يتطلب إنشاء صوت مخصص ومعاينة الأصوات وجود رصيد في حسابك.',
      'تُقفَل الإعدادات أثناء تشغيل الجلسة. أوقف الجلسة لتغيير الصوت.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'لماذا تستخدم الجلسة صوتًا مدمجًا بدلًا من «صوتي»؟',
        answer:
          'يخبرك Sokuji عندما يحدث ذلك وبالسبب. أكثر الأسباب شيوعًا: لا توجد على هذا الجهاز نسخة من تسجيلك (سجّله مرة أخرى في الإعدادات لاستخدام صوتك على هذا الكمبيوتر)، أو أن جميع خانات الأصوات المخصصة مشغولة في الوقت الحالي، أو تعذّر بناء الصوت من المقطع (جرّب تسجيلًا أوضح). تستمر الجلسة بصوت مدمج.',
      },
      {
        question: 'هل يجب أن يكون التسجيل بلغة الترجمة؟',
        answer:
          'لا. صوتك لا يرتبط بلغة التسجيل: إذا استنسختَه من تسجيل تتحدث فيه بالعربية، فسيقول ترجمتك الإنجليزية بصوتك. إن استطعت، فسجّل نفسك وأنت تتحدث بلغة الترجمة، فهكذا يبدو أقرب ما يكون إليك وأنت تتحدث تلك اللغة.',
      },
      {
        question: 'هل يمكنني استنساخ صوت شخص آخر؟',
        answer: 'فقط بإذنه. تؤكد أن لديك الحق في استخدام الصوت قبل استنساخه.',
      },
      {
        question: 'هل يمكن قراءة ترجمة الشخص الآخر بصوتي المستنسخ؟',
        answer: 'لا. لا يعرض «الصوت الذي أسمعه» إلا الأصوات الجاهزة. صوتك المستنسخ ينطق ترجمتك أنت.',
      },
      {
        question: 'هل يعمل صوتي في كل المناطق؟',
        answer:
          'مع محرك Soniox، ينتمي الصوت المخصص إلى منطقة واحدة، وتتذكر كل منطقة اختيارها الخاص للصوت. إذا غيّرت المنطقة، فأضف صوتك مرة أخرى هناك. أما «الصوت الذي أسمعه» فهو اختيار واحد لكل المناطق. وتعمل أصوات محرك Qwen في منطقتيه كلتيهما.',
      },
      {
        question: 'ماذا عن محرك Qwen؟',
        answer:
          'مع «KizunaAI — مدعوم من Qwen»، تعرض «إعدادات الصوت» 47 صوتًا جاهزًا وخيارَي استنساخ: <strong>استنساخ صوت المتحدث</strong> يستنسخ صوتك مباشرةً في كل دور، و<strong>استنساخ صوت المتحدث الأول</strong> يستنسخه مرة واحدة عند البدء ويحتفظ به — دون الحاجة إلى تسجيل أي شيء، ودون تخزين أي شيء، ودون خانة صوت مخصص. ويتحدث كل صوت جاهز بكل اللغات التي يستطيع Qwen نطقها. ويختار «الصوت الذي أسمعه» صوت الطرف الآخر بالطريقة نفسها، مع الاستنساخ.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Escolha uma voz ou clone a sua',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Quando o Sokuji fala uma tradução, a KizunaAI usa uma voz: para a sua tradução quando <strong>Tradução que o outro lado ouve</strong> está ativada (modo Eu ou Ambos), e para a da outra pessoa quando <strong>Tradução que eu ouço</strong> está ativada. Você pode escolher uma das cerca de 200 vozes predefinidas ou clonar a sua própria voz a partir de uma gravação curta, para que a outra pessoa ouça a tradução com a sua voz. Tudo isso fica em <strong>Configurações → Avançado → Provedor</strong> e descreve o mecanismo Soniox; as vozes do mecanismo Qwen são tratadas na última pergunta abaixo. As capturas de tela são do Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Abra as configurações de voz',
      content:
        'Abra <strong>Configurações</strong>, mude para <strong>Avançado</strong> com o botão no canto superior direito do painel e escolha a aba <strong>Provedor</strong>. <strong>Configurações de Voz</strong> mostra a voz em uso, com uma breve descrição de como ela soa. A seção <strong>Voz</strong> também leva até lá: abaixo de cada linha falada, <strong>Voz</strong> aparece seguido do nome da voz, e clicar no nome abre Configurações de Voz.',
      screenshot: '01-voice.png',
    },
    {
      title: 'Navegue pelas vozes predefinidas e filtre-as',
      content:
        'Clique na voz para abrir a lista. Filtre por gênero, idade, sotaque, uso e estilo, e clique no botão de reprodução para ouvir uma amostra. As prévias são sintetizadas para você e cobradas do saldo da sua conta.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Adicione a sua própria voz',
      content:
        'Em <strong>Vozes personalizadas (0/1)</strong>, escolha <strong>Adicionar uma voz…</strong>. Importe um arquivo de áudio com <strong>Importar voz…</strong>, grave na hora com <strong>Gravar voz…</strong> ou solte um arquivo na caixa de diálogo. O trecho deve ter entre 3 segundos e 2 minutos, e no máximo 35 MB.',
      screenshot: '03-add-voice.png',
      tip: 'Uma gravação nítida de você falando com naturalidade, em um ambiente silencioso, dá o melhor resultado.',
    },
    {
      title: 'Ouça, confirme e clone',
      content:
        'Ouça o trecho, marque <strong>Confirmo que tenho o direito de usar esta voz</strong> e clique em <strong>Clonar voz</strong>. A gravação é enviada à Kizuna AI e repassada à Soniox para criar a sua voz. Ela não é armazenada nos servidores da Kizuna AI; fica neste dispositivo para que a sua voz possa ser recriada depois.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'Use “Minha voz”',
      content:
        'O seu clone aparece como <strong>Minha voz</strong> em <strong>Vozes personalizadas (1/1)</strong>. Selecione-o como qualquer voz predefinida; o botão de reprodução toca uma prévia e a lixeira o exclui.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Substitua a sua voz',
      content:
        'Cada conta tem uma voz personalizada. Enquanto você a tem, a lista mostra <strong>Vozes personalizadas (1/1)</strong> e não oferece <strong>Adicionar uma voz…</strong>. Para usar uma nova gravação, primeiro exclua <strong>Minha voz</strong> com a lixeira: <strong>Adicionar uma voz…</strong> então volta a aparecer.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Escolha a voz que você ouve',
      content:
        'Enquanto a tradução da outra pessoa é lida para você (<strong>Tradução que eu ouço</strong> ativada), Configurações de Voz tem duas linhas. <strong>Voz que o outro lado ouve</strong> é a voz da sua tradução: uma predefinida ou Minha voz. <strong>Voz que eu ouço</strong> lê para você a tradução da outra pessoa e oferece apenas as vozes predefinidas. Por padrão, as duas são vozes diferentes, para que você distinga as duas pessoas; se você escolher a mesma voz para ambas, uma nota abaixo de Voz que eu ouço indica a voz que lê no lugar dela.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Ajuste a velocidade da fala',
      content:
        'Em <strong>Configurações Síntese de fala (TTS)</strong>, <strong>Velocidade da fala</strong> deixa a voz traduzida mais lenta ou mais rápida, de 0,7× a 1,3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Dicas',
    items: [
      'Uma voz só é usada quando uma tradução é falada. Com Tradução que o outro lado ouve e Tradução que eu ouço desativadas, nenhuma voz é necessária.',
      'Criar uma voz personalizada e ouvir prévias de vozes exigem saldo na sua conta.',
      'As configurações ficam bloqueadas enquanto uma sessão está em andamento. Pare a sessão para trocar a voz.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Por que uma sessão usa uma voz integrada em vez de Minha voz?',
        answer:
          'O Sokuji avisa quando isso acontece e por quê. Os motivos mais comuns: este dispositivo não tem uma cópia da sua gravação (grave de novo em Configurações para usar a sua voz neste computador), todas as vagas de voz personalizada estão ocupadas no momento, ou não foi possível criar a voz a partir do trecho (tente uma gravação mais nítida). A sessão continua com uma voz integrada.',
      },
      {
        question: 'A gravação precisa estar no idioma da minha tradução?',
        answer:
          'Não. Sua voz não depende do idioma da gravação: clonada com você falando português, ela ainda diz sua tradução para o inglês com a sua voz. Se puder, grave-se falando no idioma da sua tradução: assim ela soa o mais parecida possível com você falando esse idioma.',
      },
      {
        question: 'Posso clonar a voz de outra pessoa?',
        answer: 'Só com a permissão dela. Você confirma que tem o direito de usar a voz antes de ela ser clonada.',
      },
      {
        question: 'A tradução da outra pessoa pode ser lida com a minha voz clonada?',
        answer: 'Não. Voz que eu ouço oferece apenas as vozes predefinidas. A sua voz clonada fala a sua própria tradução.',
      },
      {
        question: 'A minha voz funciona em todas as regiões?',
        answer:
          'Com o mecanismo Soniox, as vozes personalizadas pertencem a uma região, e cada região guarda a sua própria escolha de voz. Se você mudar de região, adicione a sua voz de novo lá. Voz que eu ouço é uma única escolha para todas as regiões. As vozes do mecanismo Qwen funcionam nas duas regiões que ele oferece.',
      },
      {
        question: 'E o mecanismo Qwen?',
        answer:
          'Com a KizunaAI (Com tecnologia Qwen), Configurações de Voz oferece 47 vozes predefinidas e duas opções de clonagem: <strong>Clonar a voz de quem fala</strong> clona a sua voz ao vivo a cada turno, e <strong>Clonar a voz de quem fala primeiro</strong> a clona uma vez, no início, e a mantém — nada para gravar, nada armazenado, nenhuma vaga de voz personalizada. Cada voz predefinida fala todos os idiomas que a Qwen consegue falar. Voz que eu ouço escolhe a voz que lê a tradução da outra pessoa do mesmo jeito, clonagem incluída.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Выберите голос или клонируйте свой',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Когда Sokuji озвучивает перевод, KizunaAI использует голос: для вашего перевода — когда включён <strong>Перевод, который слышит собеседник</strong> (режим «Я» или «Оба»), а для перевода собеседника — когда включён <strong>Перевод, который слышу я</strong>. Можно выбрать один из примерно 200 готовых голосов или клонировать собственный голос по короткой записи, чтобы собеседник слышал перевод вашим голосом. Всё это находится в разделе <strong>Настройки → Расширенный → Поставщик</strong> и описывает движок Soniox; голоса движка Qwen разобраны в последнем вопросе ниже. Снимки экрана сделаны в Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Откройте настройки голоса',
      content:
        'Откройте <strong>Настройки</strong>, переключитесь в режим <strong>Расширенный</strong> кнопкой в правом верхнем углу панели и выберите вкладку <strong>Поставщик</strong>. В разделе <strong>Настройки голоса</strong> показан текущий голос с кратким описанием его звучания. Туда же ведёт и блок <strong>Речь</strong>: под каждой озвучиваемой строкой за словом <strong>Голос</strong> следует название голоса, и нажатие на название открывает «Настройки голоса».',
      screenshot: '01-voice.png',
    },
    {
      title: 'Просмотрите и отфильтруйте готовые голоса',
      content:
        'Нажмите на голос, чтобы открыть список. Сузьте его по полу, возрасту, акценту, назначению и стилю и нажмите кнопку воспроизведения, чтобы прослушать образец. Прослушивания синтезируются специально для вас и списываются с баланса учётной записи.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Добавьте свой голос',
      content:
        'В разделе <strong>Пользовательские голоса (0/1)</strong> выберите <strong>Добавить голос…</strong>. Импортируйте аудиофайл через <strong>Импортировать голос…</strong>, сразу запишите голос через <strong>Записать голос…</strong> или перетащите файл в диалоговое окно. Запись должна длиться от 3 секунд до 2 минут и весить не более 35 МБ.',
      screenshot: '03-add-voice.png',
      tip: 'Лучший результат даёт чистая запись вашей естественной речи в тихой комнате.',
    },
    {
      title: 'Прослушайте, подтвердите и клонируйте',
      content:
        'Прослушайте запись, отметьте <strong>Подтверждаю, что у меня есть право использовать этот голос</strong> и нажмите <strong>Клонировать голос</strong>. Запись отправляется в Kizuna AI и передаётся в Soniox для создания вашего голоса. На серверах Kizuna AI она не хранится; она остаётся на этом устройстве, чтобы голос можно было создать заново позже.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'Используйте «Мой голос»',
      content:
        'Ваш клон появляется как <strong>Мой голос</strong> в разделе <strong>Пользовательские голоса (1/1)</strong>. Выбирайте его так же, как готовый голос; кнопка воспроизведения даёт прослушать его, а корзина удаляет.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Замените свой голос',
      content:
        'У каждой учётной записи один пользовательский голос. Пока он у вас есть, список называется <strong>Пользовательские голоса (1/1)</strong> и не предлагает <strong>Добавить голос…</strong>. Чтобы использовать новую запись, сначала удалите <strong>Мой голос</strong> корзиной: после этого <strong>Добавить голос…</strong> появится снова.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Выберите голос, который слышите вы',
      content:
        'Пока вам зачитывается перевод собеседника (<strong>Перевод, который слышу я</strong> включён), в разделе «Настройки голоса» две строки. <strong>Голос, который слышит собеседник</strong> — голос вашего перевода: готовый голос или «Мой голос». <strong>Голос, который слышу я</strong> зачитывает вам перевод собеседника и предлагает только готовые голоса. По умолчанию это два разных голоса, чтобы вы могли различать двух людей; если выбрать для обоих один и тот же голос, под строкой «Голос, который слышу я» появится примечание с названием голоса, который будет читать вместо него.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Настройте скорость речи',
      content:
        'В разделе <strong>Настройки Синтез речи (TTS)</strong> параметр <strong>Скорость речи</strong> замедляет или ускоряет переведённый голос — от 0.7× до 1.3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Советы',
    items: [
      'Голос нужен только тогда, когда перевод озвучивается. Если «Перевод, который слышит собеседник» и «Перевод, который слышу я» оба выключены, голос не нужен.',
      'Для создания пользовательского голоса и прослушивания голосов нужен баланс на учётной записи.',
      'Во время сеанса настройки заблокированы. Чтобы сменить голос, остановите сеанс.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Почему в сеансе используется встроенный голос вместо «Мой голос»?',
        answer:
          'Sokuji сообщает, когда это происходит и почему. Самые частые причины: на этом устройстве нет копии вашей записи (запишите её снова в настройках, чтобы использовать свой голос на этом компьютере), все места для пользовательских голосов сейчас заняты или по записи не удалось создать голос (попробуйте более чистую запись). Сеанс продолжается со встроенным голосом.',
      },
      {
        question: 'Нужно ли записывать голос на языке перевода?',
        answer:
          'Нет. Голос не привязан к языку записи: если клонировать его по записи на русском, он всё равно произнесёт ваш перевод на английский вашим голосом. Если можете, запишите себя на языке перевода: тогда он звучит больше всего так, будто на этом языке говорите вы сами.',
      },
      {
        question: 'Можно ли клонировать чужой голос?',
        answer: 'Только с разрешения его владельца. Перед клонированием вы подтверждаете, что у вас есть право использовать этот голос.',
      },
      {
        question: 'Можно ли зачитывать перевод собеседника моим клонированным голосом?',
        answer: 'Нет. «Голос, который слышу я» предлагает только готовые голоса. Ваш клонированный голос произносит ваш собственный перевод.',
      },
      {
        question: 'Работает ли мой голос во всех регионах?',
        answer:
          'С движком Soniox пользовательские голоса привязаны к одному региону, и каждый регион запоминает свой выбор голоса. Если вы смените регион, добавьте голос там заново. Выбор «Голос, который слышу я» один для всех регионов. Голоса движка Qwen работают в обоих его регионах.',
      },
      {
        question: 'А что с движком Qwen?',
        answer:
          'В KizunaAI на базе Qwen в разделе «Настройки голоса» есть 47 готовых голосов и два варианта клонирования: <strong>Клонировать голос говорящего</strong> клонирует ваш голос вживую в каждой реплике, а <strong>Клонировать голос первого говорящего</strong> клонирует его один раз в начале и сохраняет — записывать ничего не нужно, ничего не хранится, места для пользовательского голоса не требуется. Каждый готовый голос говорит на всех языках, на которых может говорить Qwen. «Голос, который слышу я» выбирает голос для собеседника так же, включая клонирование.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Stimme wählen oder eigene Stimme klonen',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Wenn Sokuji eine Übersetzung spricht, verwendet KizunaAI eine Stimme: für Ihre Übersetzung, wenn <strong>Übersetzung, die das Gegenüber hört</strong> eingeschaltet ist (Modus „Ich“ oder „Beides“), und für die der anderen Seite, wenn <strong>Übersetzung, die ich höre</strong> eingeschaltet ist. Sie können eine von rund 200 vordefinierten Stimmen wählen oder Ihre eigene Stimme aus einer kurzen Aufnahme klonen, sodass die andere Seite die Übersetzung mit Ihrer Stimme hört. All das finden Sie unter <strong>Einstellungen → Erweitert → Anbieter</strong> und bezieht sich auf die Soniox-Engine; die Stimmen der Qwen-Engine behandelt die letzte Frage unten. Die Screenshots stammen aus Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Stimmeinstellungen öffnen',
      content:
        'Öffnen Sie die <strong>Einstellungen</strong>, wechseln Sie mit der Schaltfläche oben rechts im Bereich zu <strong>Erweitert</strong> und wählen Sie den Tab <strong>Anbieter</strong>. <strong>Stimmeinstellungen</strong> zeigt die verwendete Stimme mit einer kurzen Beschreibung ihres Klangs. Auch der Bereich <strong>Sprache</strong> führt dorthin: Unter jeder gesprochenen Zeile folgt auf <strong>Stimme</strong> der Name der Stimme, und ein Klick auf den Namen öffnet die Stimmeinstellungen.',
      screenshot: '01-voice.png',
    },
    {
      title: 'Vordefinierte Stimmen durchsuchen und filtern',
      content:
        'Klicken Sie auf die Stimme, um die Liste zu öffnen. Grenzen Sie sie nach Geschlecht, Alter, Akzent, Einsatzzweck und Stil ein und klicken Sie auf die Wiedergabetaste, um eine Hörprobe abzuspielen. Hörproben werden für Sie synthetisiert und von Ihrem Kontoguthaben abgebucht.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Eigene Stimme hinzufügen',
      content:
        'Wählen Sie unter <strong>Benutzerdefinierte Stimmen (0/1)</strong> die Option <strong>Stimme hinzufügen…</strong>. Importieren Sie eine Audiodatei mit <strong>Stimme importieren…</strong>, nehmen Sie direkt mit <strong>Stimme aufnehmen…</strong> auf oder ziehen Sie eine Datei in den Dialog. Die Aufnahme muss zwischen 3 Sekunden und 2 Minuten lang und höchstens 35 MB groß sein.',
      screenshot: '03-add-voice.png',
      tip: 'Das beste Ergebnis liefert eine klare Aufnahme, in der Sie natürlich sprechen, in einem ruhigen Raum.',
    },
    {
      title: 'Anhören, bestätigen und klonen',
      content:
        'Hören Sie sich die Aufnahme an, setzen Sie das Häkchen bei <strong>Ich bestätige, dass ich das Recht habe, diese Stimme zu verwenden</strong> und klicken Sie auf <strong>Stimme klonen</strong>. Die Aufnahme wird an Kizuna AI gesendet und an Soniox weitergegeben, um Ihre Stimme zu erstellen. Sie wird nicht auf den Servern von Kizuna AI gespeichert; sie bleibt auf diesem Gerät, damit Ihre Stimme später neu erstellt werden kann.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: '„Meine Stimme“ verwenden',
      content:
        'Ihr Klon erscheint als <strong>Meine Stimme</strong> unter <strong>Benutzerdefinierte Stimmen (1/1)</strong>. Wählen Sie ihn wie jede vordefinierte Stimme aus; die Wiedergabetaste spielt eine Hörprobe ab, der Papierkorb löscht ihn.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Stimme ersetzen',
      content:
        'Jedes Konto hat eine benutzerdefinierte Stimme. Solange Sie sie haben, zeigt die Liste <strong>Benutzerdefinierte Stimmen (1/1)</strong> und bietet kein <strong>Stimme hinzufügen…</strong> an. Um eine neue Aufnahme zu verwenden, löschen Sie zuerst <strong>Meine Stimme</strong> mit dem Papierkorb: Dann erscheint <strong>Stimme hinzufügen…</strong> wieder.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Die Stimme wählen, die Sie hören',
      content:
        'Während Ihnen die Übersetzung der anderen Seite vorgelesen wird (<strong>Übersetzung, die ich höre</strong> eingeschaltet), haben die Stimmeinstellungen zwei Zeilen. <strong>Stimme, die das Gegenüber hört</strong> ist die Stimme Ihrer Übersetzung: eine vordefinierte Stimme oder „Meine Stimme“. <strong>Stimme, die ich höre</strong> liest Ihnen die Übersetzung Ihres Gegenübers vor und bietet nur die vordefinierten Stimmen an. Standardmäßig sind es zwei verschiedene Stimmen, sodass Sie die beiden Personen auseinanderhalten können; wählen Sie für beide dieselbe Stimme, nennt ein Hinweis unter „Stimme, die ich höre“ die Stimme, die stattdessen liest.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Sprechgeschwindigkeit anpassen',
      content:
        'Unter <strong>Sprachsynthese (TTS)-Einstellungen</strong> macht <strong>Sprechgeschwindigkeit</strong> die übersetzte Stimme langsamer oder schneller, von 0.7× bis 1.3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Tipps',
    items: [
      'Eine Stimme wird nur verwendet, wenn eine Übersetzung gesprochen wird. Sind „Übersetzung, die das Gegenüber hört“ und „Übersetzung, die ich höre“ beide ausgeschaltet, ist keine Stimme nötig.',
      'Für das Erstellen einer benutzerdefinierten Stimme und für Hörproben brauchen Sie Guthaben auf Ihrem Konto.',
      'Während eine Sitzung läuft, sind die Einstellungen gesperrt. Beenden Sie die Sitzung, um die Stimme zu wechseln.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Warum verwendet eine Sitzung eine integrierte Stimme statt „Meine Stimme“?',
        answer:
          'Sokuji zeigt an, wann das passiert und warum. Die häufigsten Gründe: Auf diesem Gerät gibt es keine Kopie Ihrer Aufnahme (nehmen Sie sie in den Einstellungen erneut auf, um Ihre Stimme auf diesem Computer zu verwenden), alle Plätze für benutzerdefinierte Stimmen sind gerade belegt, oder aus der Aufnahme konnte keine Stimme erstellt werden (versuchen Sie es mit einer klareren Aufnahme). Die Sitzung läuft mit einer integrierten Stimme weiter.',
      },
      {
        question: 'Muss meine Aufnahme in der Sprache meiner Übersetzung sein?',
        answer:
          'Nein. Ihre Stimme ist nicht an die Sprache der Aufnahme gebunden: Aus einer deutschen Aufnahme geklont, spricht sie Ihre englische Übersetzung trotzdem mit Ihrer Stimme. Wenn möglich, nehmen Sie sich in der Sprache Ihrer Übersetzung auf: Dann klingt es am ehesten so, als würden Sie diese Sprache selbst sprechen.',
      },
      {
        question: 'Kann ich die Stimme einer anderen Person klonen?',
        answer: 'Nur mit ihrer Erlaubnis. Bevor die Stimme geklont wird, bestätigen Sie, dass Sie das Recht haben, sie zu verwenden.',
      },
      {
        question: 'Kann die Übersetzung meines Gegenübers mit meiner geklonten Stimme vorgelesen werden?',
        answer: 'Nein. „Stimme, die ich höre“ bietet nur die vordefinierten Stimmen an. Ihre geklonte Stimme spricht Ihre eigene Übersetzung.',
      },
      {
        question: 'Funktioniert meine Stimme in jeder Region?',
        answer:
          'Mit der Soniox-Engine gehören benutzerdefinierte Stimmen zu einer Region, und jede Region merkt sich ihre eigene Stimmauswahl. Wenn Sie die Region wechseln, fügen Sie Ihre Stimme dort erneut hinzu. „Stimme, die ich höre“ ist eine einzige Auswahl für alle Regionen. Die Stimmen der Qwen-Engine funktionieren in beiden ihrer Regionen.',
      },
      {
        question: 'Wie sieht es mit der Qwen-Engine aus?',
        answer:
          'Bei KizunaAI — Unterstützt von Qwen bieten die Stimmeinstellungen 47 vordefinierte Stimmen und zwei Klon-Optionen: <strong>Stimme des Sprechers klonen</strong> klont Ihre Stimme live bei jedem Redebeitrag, und <strong>Stimme des ersten Sprechers klonen</strong> klont sie einmal zu Beginn und behält sie — nichts aufzunehmen, nichts gespeichert, kein Platz für eine benutzerdefinierte Stimme. Jede vordefinierte Stimme spricht jede Sprache, die Qwen sprechen kann. „Stimme, die ich höre“ wählt die Stimme für die andere Seite auf dieselbe Weise, einschließlich Klonen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Виберіть голос або клонуйте свій',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Коли Sokuji озвучує переклад, KizunaAI використовує голос: для вашого перекладу, коли ввімкнено <strong>Переклад, який чує співрозмовник</strong> (режим «Я» або «Обидва»), і для перекладу співрозмовника, коли ввімкнено <strong>Переклад, який чую я</strong>. Можна вибрати один із приблизно 200 готових голосів або клонувати власний голос за коротким записом, щоб співрозмовник чув переклад вашим голосом. Усе це розташовано в розділі <strong>Налаштування → Розширений → Постачальник</strong> і стосується рушія Soniox; голоси рушія Qwen описано в останньому запитанні нижче. Знімки екрана зроблено в Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Відкрийте налаштування голосу',
      content:
        'Відкрийте <strong>Налаштування</strong>, перейдіть у режим <strong>Розширений</strong> кнопкою в правому верхньому куті панелі та виберіть вкладку <strong>Постачальник</strong>. У розділі <strong>Налаштування голосу</strong> показано поточний голос із коротким описом його звучання. Туди ж веде й блок <strong>Мовлення</strong>: під кожним озвучуваним рядком після <strong>Голос</strong> указано назву голосу, а натискання на назву відкриває «Налаштування голосу».',
      screenshot: '01-voice.png',
    },
    {
      title: 'Перегляньте й відфільтруйте готові голоси',
      content:
        'Натисніть на голос, щоб відкрити список. Звузьте його за статтю, віком, акцентом, призначенням і стилем та натисніть кнопку відтворення, щоб прослухати зразок. Прослуховування синтезуються спеціально для вас і списуються з балансу облікового запису.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Додайте свій голос',
      content:
        'У розділі <strong>Користувацькі голоси (0/1)</strong> виберіть <strong>Додати голос…</strong>. Імпортуйте аудіофайл через <strong>Імпортувати голос…</strong>, одразу запишіть голос через <strong>Записати голос…</strong> або перетягніть файл у діалогове вікно. Запис має тривати від 3 секунд до 2 хвилин і важити не більше ніж 35 МБ.',
      screenshot: '03-add-voice.png',
      tip: 'Найкращий результат дає чистий запис вашого природного мовлення в тихій кімнаті.',
    },
    {
      title: 'Прослухайте, підтвердьте й клонуйте',
      content:
        'Прослухайте запис, позначте <strong>Підтверджую, що маю право використовувати цей голос</strong> і натисніть <strong>Клонувати голос</strong>. Запис надсилається до Kizuna AI і передається до Soniox для створення вашого голосу. На серверах Kizuna AI він не зберігається; він залишається на цьому пристрої, щоб голос можна було створити знову пізніше.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'Використовуйте «Мій голос»',
      content:
        'Ваш клон з’являється як <strong>Мій голос</strong> у розділі <strong>Користувацькі голоси (1/1)</strong>. Вибирайте його так само, як готовий голос; кнопка відтворення дає його прослухати, а кошик видаляє.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Замініть свій голос',
      content:
        'Кожен обліковий запис має один користувацький голос. Поки він у вас є, список має назву <strong>Користувацькі голоси (1/1)</strong> і не пропонує <strong>Додати голос…</strong>. Щоб використати новий запис, спочатку видаліть <strong>Мій голос</strong> кошиком: тоді <strong>Додати голос…</strong> з’явиться знову.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Виберіть голос, який чуєте ви',
      content:
        'Поки переклад співрозмовника зачитується вам (<strong>Переклад, який чую я</strong> ввімкнено), розділ «Налаштування голосу» має два рядки. <strong>Голос, який чує співрозмовник</strong> — це голос вашого перекладу: готовий голос або «Мій голос». <strong>Голос, який чую я</strong> зачитує вам переклад співрозмовника й пропонує лише готові голоси. За замовчуванням це два різні голоси, щоб ви могли розрізняти двох людей; якщо вибрати той самий голос для обох, примітка під «Голос, який чую я» назве голос, який читатиме замість нього.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Налаштуйте швидкість мовлення',
      content:
        'У розділі <strong>Налаштування Синтез мовлення (TTS)</strong> параметр <strong>Швидкість мовлення</strong> сповільнює або пришвидшує перекладений голос — від 0.7× до 1.3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Поради',
    items: [
      'Голос потрібен лише тоді, коли переклад озвучується. Якщо «Переклад, який чує співрозмовник» і «Переклад, який чую я» вимкнено, голос не потрібен.',
      'Для створення користувацького голосу й прослуховування голосів потрібен баланс на обліковому записі.',
      'Під час сеансу налаштування заблоковано. Щоб змінити голос, зупиніть сеанс.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чому в сеансі використовується вбудований голос замість «Мій голос»?',
        answer:
          'Sokuji повідомляє, коли це стається і чому. Найпоширеніші причини: на цьому пристрої немає копії вашого запису (запишіть його знову в налаштуваннях, щоб використовувати свій голос на цьому комп’ютері), усі місця для користувацьких голосів зараз зайняті або із запису не вдалося створити голос (спробуйте чистіший запис). Сеанс продовжується з вбудованим голосом.',
      },
      {
        question: 'Чи потрібно записувати голос мовою перекладу?',
        answer:
          'Ні. Голос не прив’язаний до мови запису: якщо клонувати його із запису українською, він однаково промовить ваш переклад англійською вашим голосом. Якщо можете, запишіть себе мовою перекладу: тоді він звучить найбільше так, ніби цією мовою говорите ви самі.',
      },
      {
        question: 'Чи можна клонувати чужий голос?',
        answer: 'Лише з дозволу його власника. Перед клонуванням ви підтверджуєте, що маєте право використовувати цей голос.',
      },
      {
        question: 'Чи можна зачитувати переклад співрозмовника моїм клонованим голосом?',
        answer: 'Ні. «Голос, який чую я» пропонує лише готові голоси. Ваш клонований голос озвучує ваш власний переклад.',
      },
      {
        question: 'Чи працює мій голос у всіх регіонах?',
        answer:
          'З рушієм Soniox користувацькі голоси прив’язані до одного регіону, і кожен регіон запам’ятовує свій вибір голосу. Якщо ви зміните регіон, додайте голос там знову. «Голос, який чую я» — один вибір для всіх регіонів. Голоси рушія Qwen працюють в обох його регіонах.',
      },
      {
        question: 'А що з рушієм Qwen?',
        answer:
          'З KizunaAI на базі Qwen «Налаштування голосу» пропонують 47 готових голосів і два варіанти клонування: <strong>Клонувати голос мовця</strong> клонує ваш голос наживо в кожній репліці, а <strong>Клонувати голос першого мовця</strong> клонує його один раз на початку й зберігає — без запису, без збереження, без місця для користувацького голосу. Кожен готовий голос говорить усіма мовами, якими вміє говорити Qwen. «Голос, який чую я» вибирає голос для співрозмовника так само, зокрема з клонуванням.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Scegli una voce o clona la tua',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Quando Sokuji pronuncia una traduzione, KizunaAI usa una voce: per la tua traduzione quando <strong>Traduzione che sente l\'altra parte</strong> è attiva (modalità Io o Entrambi), e per quella dell’altra parte quando è attiva <strong>Traduzione che sento io</strong>. Puoi scegliere una delle circa 200 voci predefinite, oppure clonare la tua voce da una breve registrazione, così l’altra parte sente la traduzione con la tua voce. Trovi tutto in <strong>Impostazioni → Avanzato → Fornitore</strong>, e questo descrive il motore Soniox; le voci del motore Qwen sono trattate nell’ultima domanda qui sotto. Le schermate provengono da Sokuji 0.44.0.',
  },
  steps: [
    {
      title: 'Apri le impostazioni della voce',
      content:
        'Apri <strong>Impostazioni</strong>, passa ad <strong>Avanzato</strong> con il pulsante in alto a destra del pannello e scegli la scheda <strong>Fornitore</strong>. <strong>Impostazioni Voce</strong> mostra la voce in uso, con una breve descrizione di come suona. Ci porta anche la sezione <strong>Voce</strong>: sotto ogni riga parlata, la dicitura <strong>Voce</strong> è seguita dal nome della voce, e facendo clic sul nome si apre Impostazioni Voce.',
      screenshot: '01-voice.png',
    },
    {
      title: 'Sfoglia e filtra le voci predefinite',
      content:
        'Fai clic sulla voce per aprire l’elenco. Restringilo per genere, età, accento, utilizzo e stile, e premi il pulsante di riproduzione per ascoltare un campione. Le anteprime vengono sintetizzate per te e addebitate sul saldo del tuo account.',
      screenshot: '02-voice-picker.png',
    },
    {
      title: 'Aggiungi la tua voce',
      content:
        'In <strong>Voci Personalizzate (0/1)</strong>, scegli <strong>Aggiungi una voce…</strong>. Importa un file audio con <strong>Importa voce…</strong>, registra subito con <strong>Registra voce…</strong> oppure trascina un file nella finestra di dialogo. La clip deve durare tra 3 secondi e 2 minuti e pesare al massimo 35 MB.',
      screenshot: '03-add-voice.png',
      tip: 'Il risultato migliore si ottiene con una registrazione nitida in cui parli in modo naturale, in una stanza silenziosa.',
    },
    {
      title: 'Ascolta, conferma e clona',
      content:
        'Ascolta la clip, spunta <strong>Confermo di avere il diritto di utilizzare questa voce</strong> e fai clic su <strong>Clona voce</strong>. La registrazione viene inviata a Kizuna AI e passata a Soniox per creare la tua voce. Non viene conservata sui server di Kizuna AI; resta su questo dispositivo, così la tua voce può essere ricreata in seguito.',
      screenshot: '04-review-and-consent.png',
    },
    {
      title: 'Usa «La mia voce»',
      content:
        'Il tuo clone compare come <strong>La mia voce</strong> in <strong>Voci Personalizzate (1/1)</strong>. Selezionalo come qualsiasi voce predefinita; il pulsante di riproduzione ne fa ascoltare un’anteprima e il cestino lo elimina.',
      screenshot: '05-my-voice.png',
    },
    {
      title: 'Sostituisci la tua voce',
      content:
        'Ogni account ha una sola voce personalizzata. Finché ce l’hai, l’elenco riporta <strong>Voci Personalizzate (1/1)</strong> e non offre <strong>Aggiungi una voce…</strong>. Per usare una nuova registrazione, elimina prima <strong>La mia voce</strong> con il cestino: <strong>Aggiungi una voce…</strong> allora ricompare.',
      screenshot: '06-replace-hint.png',
    },
    {
      title: 'Scegli la voce che senti',
      content:
        'Mentre la traduzione dell’altra parte ti viene letta (<strong>Traduzione che sento io</strong> attiva), Impostazioni Voce ha due righe. <strong>Voce che sente l\'altra parte</strong> è la voce della tua traduzione: una voce predefinita o La mia voce. <strong>Voce che sento io</strong> ti legge la traduzione dell’altra persona e offre solo le voci predefinite. Per impostazione predefinita le due voci sono diverse, così puoi distinguere le due persone; se scegli la stessa voce per entrambe, una nota sotto Voce che sento io indica la voce che legge al suo posto.',
      screenshot: '08-two-voices.png',
    },
    {
      title: 'Regola la velocità del parlato',
      content:
        'In <strong>Impostazioni Sintesi vocale (TTS)</strong>, <strong>Velocità del parlato</strong> rende la voce tradotta più lenta o più veloce, da 0,7× a 1,3×.',
      screenshot: '07-speech-speed.png',
    },
  ],
  tips: {
    title: 'Consigli',
    items: [
      'Una voce viene usata solo quando una traduzione viene pronunciata. Con Traduzione che sente l\'altra parte e Traduzione che sento io entrambe disattivate, non serve nessuna voce.',
      'Per creare una voce personalizzata e per ascoltare le anteprime delle voci serve un saldo sul tuo account.',
      'Le impostazioni sono bloccate mentre una sessione è attiva. Interrompi la sessione per cambiare voce.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Perché una sessione usa una voce integrata invece di La mia voce?',
        answer:
          'Sokuji ti avvisa quando succede e perché. I motivi più comuni: questo dispositivo non ha una copia della tua registrazione (registrala di nuovo in Impostazioni per usare la tua voce su questo computer), tutti gli slot per le voci personalizzate sono momentaneamente occupati, oppure non è stato possibile creare la voce dalla clip (prova una registrazione più nitida). La sessione prosegue con una voce integrata.',
      },
      {
        question: 'La registrazione deve essere nella lingua della mia traduzione?',
        answer:
          'No. La tua voce non dipende dalla lingua della registrazione: clonata mentre parli italiano, pronuncia comunque la tua traduzione in inglese con la tua voce. Se puoi, registrati mentre parli nella lingua della tua traduzione: così suona il più possibile come te che parli quella lingua.',
      },
      {
        question: 'Posso clonare la voce di un’altra persona?',
        answer: 'Solo con il suo permesso. Prima che la voce venga clonata, confermi di avere il diritto di utilizzarla.',
      },
      {
        question: 'La traduzione dell’altra persona può essere letta con la mia voce clonata?',
        answer: 'No. Voce che sento io offre solo le voci predefinite. La tua voce clonata pronuncia la tua traduzione.',
      },
      {
        question: 'La mia voce funziona in ogni regione?',
        answer:
          'Con il motore Soniox, le voci personalizzate appartengono a una sola regione, e ogni regione ricorda la propria scelta di voce. Se cambi regione, aggiungi di nuovo lì la tua voce. Voce che sento io è un’unica scelta per tutte le regioni. Le voci del motore Qwen funzionano in entrambe le sue regioni.',
      },
      {
        question: 'E il motore Qwen?',
        answer:
          'Se scegli KizunaAI con tecnologia Qwen, Impostazioni Voce offre 47 voci predefinite e due modi di clonare: <strong>Clona la voce di chi parla</strong> clona la tua voce dal vivo a ogni turno, e <strong>Clona la voce di chi parla per primo</strong> la clona una sola volta all’inizio e la mantiene — niente da registrare, niente archiviato, nessuno slot per voci personalizzate. Ogni voce predefinita parla ogni lingua che Qwen sa parlare. Voce che sento io sceglie la voce per l’altra parte allo stesso modo, clonazione compresa.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function KizunaVoicesGuide() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/kizuna-ai-voices" />;
}
